import styled from '@emotion/styled'
import { CsvParserErrorType } from '@pubstack/common/src/csv-parser-error'
import { AD_UNIT_NAME_ERROR, AdUnitEnv, CreatePbsAdUnit, PBS_AD_UNIT_CSV_HEADERS, PBS_AD_UNIT_CSV_INAPP_HEADERS } from '@pubstack/common/src/pbs/adunit'
import { FC, useMemo, useState } from 'react'
import { useImportAdUnitsFromCsv } from '~/api/pbs/ad-units.api'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { Sizes } from '~/assets/style/tokens'
import { Alert } from '~/components/Alert'
import Button from '~/components/Button'
import Chip from '~/components/Chip'
import { BaseFileTransfer } from '~/components/FileTransfer'
import { Flyout } from '~/components/Flyout'
import { Icon, IconName } from '~/components/Icon'
import { useToast } from '~/components/Toast/useToasts'
import { Tooltip } from '~/components/Tooltip'
import { useAdFormat } from '~/modules/adm/inventory/adunits/AdFormatDisplay'
import { downloadCSVData } from '~/utils/fileDownload'

const Container = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${Colors.White};
  padding: 0.75rem;
  gap: 1rem;
  margin: -10px;
  box-shadow: 0 3px 10px rgba(54, 54, 54, 0.3);
  border-radius: 4px;
  color: ${Colors.Jet};
  width: 400px;
`

const IconTextRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`
const SectionTitle = styled.div`
  ${Fonts.P2}
  color: ${Colors.Jet};
  font-weight: 700;
  margin-bottom: 0.25rem;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const ErrorList = styled.ul`
  padding: 0;
  margin: 0 0 1rem 1rem;
  list-style: inside;
`

const Tips = styled.p`
  color: ${Colors.Hurricane};
  font-style: italic;
  margin-bottom: 1rem;
`

const ErrorListDisplay: FC<{ errors: CsvParserErrorType[] }> = ({ errors }) => (
  <ErrorList>
    {errors.map((e, idx) => {
      if (typeof e === 'string') return <li key={idx}>{e}</li>
      const { row, actualValue, message } = e
      if (row && actualValue) return <li key={idx}>{`l${row}: "${actualValue}" ${message}`}</li>
      if (row) return <li key={idx}>{`l${row}: ${message}`}</li>
      return <li key={idx}>{message}</li>
    })}
  </ErrorList>
)

type Props = {
  onClose: () => void
  env: Exclude<AdUnitEnv, 'web'>
}

export const ImportAdUnitsCsvFlyout: FC<Props> = ({ onClose, env }) => {
  const [adUnitsFile, setAdUnitsFile] = useState<File | null>()
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  const { mutateAsync: validateCsv, data: adunitToBeCreated, isSuccess: isValidated, error } = useImportAdUnitsFromCsv(env, true)
  const { mutateAsync: importCsv, isPending: isCreating } = useImportAdUnitsFromCsv(env, false)
  const bannerIcon = useAdFormat('APP_BANNER')
  const interstitialIcon = useAdFormat('APP_INTERSTITIAL')

  const getFormatIcon = (id: string): IconName => {
    const mapping: Record<string, IconName | undefined> = {
      APP_BANNER: bannerIcon?.iconName,
      APP_INTERSTITIAL: interstitialIcon?.iconName,
    }
    return mapping[id] || 'banner'
  }

  const toast = useToast()

  const isImportDisabled = !adUnitsFile || !isValidated || !adunitToBeCreated.length

  const handleFileChange = async (file: File | null) => {
    setAdUnitsFile(file)
    if (!file) return
    try {
      const data = new FormData()
      data.append('csv', file)
      data.append('env', env)
      await validateCsv(data)
    } catch (e) {
      if (!(e.type === 'CsvParserError')) {
        toast.alert('An unknown error occurred while importing adunits.', { duration: Infinity })
      }
    }
  }

  const handleDownload = async () => {
    setIsDownloading(true)
    const data = await adUnitsFile?.text()
    if (!data) return
    downloadCSVData(data, adUnitsFile?.name)
    setIsDownloading(false)
  }

  const handleDownloadS2sTemplate = () => {
    const headers = [...PBS_AD_UNIT_CSV_HEADERS]
    const examples = [['AD_UNIT_1'], ['AD_UNIT_2'], ['AD_UNIT_3']]
    const csvContent = [headers, ...examples].map((row) => row.join(',')).join('\n') + '\n'
    downloadCSVData(csvContent, 'adunits-template.csv')
  }

  const handleDownloadInAppTemplate = () => {
    const headers = [...PBS_AD_UNIT_CSV_INAPP_HEADERS]
    const examples = [
      ['AD_UNIT_1', 'Banner'],
      ['AD_UNIT_2', 'Interstitial'],
      ['AD_UNIT_3', 'Banner'],
    ]
    const csvContent = [headers, ...examples].map((row) => row.join(',')).join('\n') + '\n'
    downloadCSVData(csvContent, 'adunits-inapp-template.csv')
  }

  const handleDownloadTemplate = {
    s2s: handleDownloadS2sTemplate,
    inapp: handleDownloadInAppTemplate,
    amp: handleDownloadS2sTemplate,
  }

  const handleDelete = () => {
    setAdUnitsFile(null)
  }

  const handleImport = async () => {
    if (!adUnitsFile) return
    try {
      const data = new FormData()
      data.append('csv', adUnitsFile)
      data.append('env', env)
      await importCsv(data)
      toast.success('Ad units successfully imported')
      onClose()
    } catch (e) {
      if (!(e.response?.data?.type === 'CsvParserError')) {
        toast.alert('An unknown error occurred while importing ad units.', { duration: Infinity })
      }
    }
  }

  const errorDisplay = useMemo(() => {
    if (!adUnitsFile || error?.type !== 'CsvParserError') return null

    return (
      <Alert variant="danger" iconName={'info'}>
        <div>
          <ErrorListDisplay errors={error.errors} />
          <Tips>Please note that ad units can't have the same name, even if the format differs.</Tips>
          <p>{AD_UNIT_NAME_ERROR}</p>
        </div>
      </Alert>
    )
  }, [adUnitsFile, error])

  const adUnitsToCreateDisplay =
    !adUnitsFile || !isValidated || !adunitToBeCreated ? null : (
      <div>
        <h3>Ad units to be created</h3>
        <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {adunitToBeCreated.map((adUnit: CreatePbsAdUnit) =>
            adUnit.adFormat ? (
              <Tooltip
                key={adUnit.name}
                title={
                  <TooltipContent>
                    <div>
                      <SectionTitle>Format</SectionTitle>
                      <IconTextRow>
                        <Icon name={getFormatIcon(adUnit.adFormat?.id)} />
                        <span>{adUnit.adFormat?.name}</span>
                      </IconTextRow>
                    </div>
                  </TooltipContent>
                }
              >
                <Chip color={Colors.Ghost} key={adUnit.name} text={adUnit.name} />
              </Tooltip>
            ) : (
              <Chip color={Colors.Ghost} key={adUnit.name} text={adUnit.name} />
            )
          )}
        </div>
      </div>
    )

  return (
    <Flyout.Content>
      <Flyout.Title>
        <Title>
          <p>Import ad units</p>
        </Title>
      </Flyout.Title>
      <Flyout.Body>
        <Alert variant={'primary'} iconName={'info'} iconSize={Sizes[20]} css={{ marginBottom: '1rem' }}>
          All ad units with the same name will only be added once to Pubstack.
        </Alert>
        <Container>
          <BaseFileTransfer
            label="Ad units"
            filename={adUnitsFile?.name}
            trackingPrefixAndLabel="pbs-ad-units-import-file"
            isDownloading={isDownloading}
            onChange={(event) => handleFileChange(event.target.files?.[0] ?? null)}
            onDownload={handleDownload}
            onDelete={handleDelete}
            onDownloadTemplate={handleDownloadTemplate[env]}
          />
          {errorDisplay}
          {adUnitsToCreateDisplay}
        </Container>
      </Flyout.Body>
      <Flyout.Actions>
        <Button iconName={isCreating ? 'pubstack' : undefined} iconSpin={isCreating} trackingLabel="pbs-import-ad-units-save" onClick={handleImport} disabled={isImportDisabled}>
          Import
        </Button>
      </Flyout.Actions>
    </Flyout.Content>
  )
}
